<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, paymentMethodLabel } from '../utils/format'
import type {
  CurrencyOption,
  Option,
  Payment,
  PaymentDirection,
  PaymentMethod,
} from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const { listPayments, listCurrencies, listAllAgentsByRole, reversePayment } = useFinanceApi()

const PER_PAGE = 20

const direction = computed<PaymentDirection>(() =>
  route.meta.direction === 'outflow' ? 'outflow' : 'inflow',
)
const basePath = computed(() =>
  direction.value === 'inflow' ? '/receivables/collections' : '/payables/payments',
)
const title = computed(() =>
  direction.value === 'inflow' ? t('finance.nav.collections') : t('finance.nav.supplierPayments'),
)

const permissions = computed(() => auth.user?.permissions ?? [])
const canCreate = computed(() => permissions.value.includes('payments.create'))
const canUpdate = computed(() => permissions.value.includes('payments.update'))
const canDelete = computed(() => permissions.value.includes('payments.delete'))

const methods: PaymentMethod[] = ['cash', 'card', 'transfer']

const rows = ref<Payment[]>([])
const currencies = ref<CurrencyOption[]>([])
const counterparties = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref('')
const page = ref(1)

const filters = ref({
  agent_id: '' as string | number,
  payment_method: '' as PaymentMethod | '',
  currency_code: '',
  search: '',
})

const visibleRows = computed(() => {
  const term = filters.value.search.trim().toLowerCase()
  if (!term) return rows.value
  return rows.value.filter((payment) =>
    [payment.reference, payment.agent_name, payment.financial_account_name, String(payment.id)]
      .filter((value) => value !== null && value !== undefined)
      .some((value) => String(value).toLowerCase().includes(term)),
  )
})

const totalPages = computed(() => Math.max(1, Math.ceil(visibleRows.value.length / PER_PAGE)))
const pagedRows = computed(() =>
  visibleRows.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE),
)

const rangeLabel = computed(() => {
  const total = visibleRows.value.length
  if (!total) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total)
  return t('finance.common.showing', { from, to, total })
})

const totalsByCurrency = computed(() => {
  const totals = new Map<string, number>()
  for (const payment of visibleRows.value) {
    const code = payment.currency_code || '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(payment.amount ?? 0))
  }
  return [...totals.entries()]
    .map(([code, amount]) => ({ code, amount }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    rows.value = await listPayments({
      agent_id: filters.value.agent_id,
      payment_method: filters.value.payment_method,
      direction: direction.value,
      currency_code: filters.value.currency_code,
    })
    page.value = 1
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadOptions() {
  const [currencyResult, counterpartyResult] = await Promise.allSettled([
    listCurrencies(),
    listAllAgentsByRole(direction.value === 'inflow' ? 'customer' : 'supplier'),
  ])
  currencies.value = currencyResult.status === 'fulfilled' ? currencyResult.value : []
  counterparties.value = counterpartyResult.status === 'fulfilled' ? counterpartyResult.value : []
}

async function reverse(payment: Payment) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('finance.payments.reverseConfirm'))) return
  busyId.value = String(payment.id)
  try {
    await reversePayment(payment.id)
    actionMessage.value = t('finance.payments.reversed')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = ''
  }
}

watch(
  () => [filters.value.agent_id, filters.value.payment_method, filters.value.currency_code],
  () => {
    void load()
  },
)

watch(direction, () => {
  void loadOptions()
  void load()
})

watch(
  () => filters.value.search,
  () => {
    page.value = 1
  },
)

onMounted(() => {
  void loadOptions()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ title }}</h2>
  </div>

  <div class="toolbar">
    <select v-model="filters.agent_id" class="filter-select">
      <option value="">{{ t('finance.common.agent') }}</option>
      <option v-for="counterparty in counterparties" :key="counterparty.id" :value="counterparty.id">
        {{ counterparty.name }}
      </option>
    </select>
    <select v-model="filters.payment_method" class="filter-select">
      <option value="">{{ t('finance.common.allMethods') }}</option>
      <option v-for="method in methods" :key="method" :value="method">
        {{ t(`finance.paymentMethod.${method}`) }}
      </option>
    </select>
    <select v-model="filters.currency_code" class="filter-select">
      <option value="">{{ t('finance.common.allCurrencies') }}</option>
      <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
        {{ currency.code }}
      </option>
    </select>
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('finance.common.searchPlaceholder')"
    >
    <span class="spacer" />
    <div class="summary">
      <span v-for="line in totalsByCurrency" :key="line.code">
        <strong>{{ formatMoney(line.amount, line.code) }}</strong>
      </span>
      <span>{{ t('finance.common.rows', { count: visibleRows.length }) }}</span>
    </div>
    <RouterLink v-if="canCreate" class="btn-primary" :to="`${basePath}/new`">
      {{ t('finance.payments.newPayment') }}
    </RouterLink>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="visibleRows.length === 0" class="state-message">
    <p>{{ t('finance.payments.empty') }}</p>
  </div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.common.date') }}</th>
            <th>{{ t('finance.common.agent') }}</th>
            <th>{{ t('finance.common.account') }}</th>
            <th>{{ t('finance.common.method') }}</th>
            <th>{{ t('finance.common.reference') }}</th>
            <th class="cell-num">{{ t('finance.common.amount') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="payment in pagedRows" :key="payment.id">
            <td class="cell-date">{{ formatDisplayDate(payment.created_at) }}</td>
            <td class="cell-name">
              {{ payment.agent_name || payment.agent_id }}
              <span class="cell-sub">#{{ payment.id }}</span>
            </td>
            <td>{{ payment.financial_account_name || payment.financial_account_id }}</td>
            <td>{{ paymentMethodLabel(t, payment.payment_method) }}</td>
            <td>{{ payment.reference || '—' }}</td>
            <td class="cell-num">{{ formatMoney(payment.amount, payment.currency_code) }}</td>
            <td class="cell-actions">
              <RouterLink
                v-if="canUpdate"
                class="link-action"
                :to="`${basePath}/${payment.id}/edit`"
              >
                {{ t('finance.common.edit') }}
              </RouterLink>
              <button
                v-if="canDelete"
                class="link-action danger"
                :disabled="busyId === String(payment.id)"
                @click="reverse(payment)"
              >
                {{ t('finance.payments.reverse') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button :disabled="page <= 1" @click="page -= 1">{{ t('finance.common.previous') }}</button>
      <span>{{ t('finance.common.page', { page, total: totalPages }) }}</span>
      <button :disabled="page >= totalPages" @click="page += 1">
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>
</template>
